import React from "react";
import { Link } from "react-router-dom";
import { Card, Kicker, Mail, Mark } from "./ui";
import { CONTACT_EMAIL } from "./constants";

const STEPS = [
  {
    n: "01",
    title: "Data arrives",
    body: "A hospital, a registry, or a researcher uploads a CSV into a project. Nothing is stored yet.",
  },
  {
    n: "02",
    title: "Identifiers are found",
    body: "The system flags columns that hold personal data — and reads inside free-text columns, where a kennitala buried in a sentence is the leak nobody catches by hand.",
  },
  {
    n: "03",
    title: "A person confirms",
    body: "The uploader reviews every finding and decides: replace the identifier, or drop the column so its raw contents are never stored at all.",
  },
  {
    n: "04",
    title: "Researchers work with pseudonyms",
    body: "The dataset enters the catalogue carrying codes instead of identities. The originals are encrypted under separate keys.",
  },
];

const ROLES = [
  {
    title: "Researchers",
    body: "Browse the dataset catalogue, download what they are cleared for, request what they are not, and pseudonymize their own files.",
  },
  {
    title: "Principal investigators",
    body: "Run projects: invite members, set access policy, review data requests, upload data, and forward requests to external providers.",
  },
  {
    title: "Department heads",
    body: "Oversight of every project, dataset, request, and user in their own department — deliberately without the ability to download research data.",
  },
  {
    title: "Data providers",
    body: "Hospital and registry staff who supply data. They see only the requests addressed to them and the upload form — never the catalogue or anyone's results.",
  },
];

/** The illustration that carries the whole idea: one person, two projects, two codes. */
const IsolationFigure: React.FC = () => (
  <figure className="rounded-2xl border border-sand-200 bg-white p-6 sm:p-8">
    <div className="grid items-center gap-6 sm:grid-cols-[1fr,auto,1fr]">
      <div className="rounded-xl border border-sand-200 bg-sand-50 p-5">
        <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-400">
          Source record
        </p>
        <p className="mt-3 font-mono text-sm text-ink">120385-3299</p>
        <p className="font-mono text-sm text-ink-600">Anna Jónsdóttir</p>
        <p className="mt-3 text-xs text-ink-400">Direct identifiers, encrypted at rest</p>
      </div>

      {/* One arrow pointing down on narrow screens, two pointing right when the
          figure is laid out in columns. */}
      <div
        className="flex justify-center py-3 text-sand-300 sm:flex-col sm:gap-10 sm:py-0"
        aria-hidden="true"
      >
        <svg width="40" height="14" viewBox="0 0 40 14" fill="none" className="rotate-90 sm:rotate-0">
          <path d="M0 7h32" stroke="currentColor" strokeWidth="1.5" />
          <path d="M31 2l6 5-6 5" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
        <svg width="40" height="14" viewBox="0 0 40 14" fill="none" className="hidden sm:block">
          <path d="M0 7h32" stroke="currentColor" strokeWidth="1.5" />
          <path d="M31 2l6 5-6 5" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      </div>

      <div className="space-y-4">
        <div className="rounded-xl border border-accent/25 bg-accent-pale/50 p-5">
          <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-600">
            Project — Heart study
          </p>
          <p className="mt-3 font-mono text-sm text-ink">subject bK9xQ2</p>
        </div>
        <div className="rounded-xl border border-accent/25 bg-accent-pale/50 p-5">
          <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-600">
            Project — Diabetes registry
          </p>
          <p className="mt-3 font-mono text-sm text-ink">subject 7pR4Lm</p>
        </div>
      </div>
    </div>
    <figcaption className="mt-6 border-t border-sand-200 pt-4 text-sm leading-relaxed text-ink-600">
      The same person, from the same source file, in two projects. Inside a project the code is
      stable, so datasets can be joined and colleagues can discuss the same subject. Across
      projects the codes never match — the two projects cannot combine their data or discover
      that they share a participant.
    </figcaption>
  </figure>
);

export const HomePage: React.FC = () => (
  <>
    {/* Hero */}
    <section className="bg-ink text-white">
      <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-28">
        <div className="max-w-3xl">
          <Kicker tone="light">Health research data · pseudonymization</Kicker>
          <h1 className="mt-5 font-display text-4xl leading-[1.08] sm:text-6xl">
            Researchers get the data.
            <br />
            Nobody gets the identities.
          </h1>
          <p className="mt-7 max-w-2xl text-lg leading-relaxed text-sand-100/80">
            Health research data arrives full of direct identifiers — kennitölur, names,
            addresses, phone numbers. Researchers need the data; they do not need the identities.
            Nafnleyndarkerfi sits between the two: every identifier is replaced with a code that
            carries no information about the person, and the real values are encrypted under
            separate keys.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/about"
              className="rounded-lg bg-accent-light px-5 py-3 text-sm font-semibold text-ink transition hover:bg-accent-pale"
            >
              How the system works
            </Link>
            <Link
              to="/login"
              className="rounded-lg border border-white/25 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
            >
              Log in
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* The one rule */}
    <section className="border-b border-sand-200 bg-white">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,26rem),minmax(0,1fr)]">
          <div>
            <Kicker>The rule that matters</Kicker>
            <h2 className="mt-4 font-display text-3xl leading-tight text-ink sm:text-[2.5rem]">
              A pseudonym is stable inside a project, and different in every other project.
            </h2>
            <p className="mt-6 leading-relaxed text-ink-600">
              This is deliberate: collaboration within an approved project, hard isolation
              between projects. Each project&rsquo;s codes come from its own cryptographic key,
              held in a dedicated key-management service.
            </p>
          </div>
          <IsolationFigure />
        </div>
      </div>
    </section>

    {/* How it works */}
    <section className="bg-sand-50">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
        <Kicker>From upload to analysis</Kicker>
        <h2 className="mt-4 font-display text-3xl text-ink sm:text-4xl">Four steps</h2>
        <div className="mt-10 grid gap-x-10 gap-y-10 sm:grid-cols-2">
          {STEPS.map((step) => (
            <div key={step.n} className="flex gap-5">
              <span className="font-display text-2xl text-sand-300 leading-none">{step.n}</span>
              <div>
                <h3 className="font-semibold text-ink">{step.title}</h3>
                <p className="mt-2 leading-relaxed text-[#3B4A48]">{step.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Roles */}
    <section className="border-y border-sand-200 bg-white">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
        <div className="max-w-2xl">
          <Kicker>Who uses it</Kicker>
          <h2 className="mt-4 font-display text-3xl text-ink sm:text-4xl">
            Four kinds of account
          </h2>
          <p className="mt-5 leading-relaxed text-ink-600">
            Access follows project membership, not seniority. Everyone belongs to exactly one
            department, and nothing crosses between departments — health sciences and social
            sciences share the software and share nothing else.
          </p>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {ROLES.map((role) => (
            <Card key={role.title} title={role.title}>
              <p>{role.body}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>

    {/* Honest limits */}
    <section className="bg-sand-100">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,22rem),minmax(0,1fr)]">
          <div>
            <Kicker>Scope</Kicker>
            <h2 className="mt-4 font-display text-3xl leading-tight text-ink">
              What the system does not do
            </h2>
          </div>
          <div className="site-prose space-y-6 border-l border-sand-300 pl-8">
            <div>
              <p className="font-semibold text-ink">It is not statistical anonymization.</p>
              <p>
                Pseudonymization protects identities, not small-cell disclosure. A dataset of
                three patients with a rare diagnosis is still sensitive, and your obligations for
                the data itself do not go away.
              </p>
            </div>
            <div>
              <p className="font-semibold text-ink">
                It does not replace ethics approval or a data processing agreement.
              </p>
              <p>
                It is a technical control that helps you meet your obligations under your IRB
                approval and under GDPR — not a substitute for either.
              </p>
            </div>
            <div>
              <p className="font-semibold text-ink">It does not decide who may see what.</p>
              <p>
                People do. Principal investigators and department heads set the policy; the
                system enforces it, records it, and makes it reviewable.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Where to next */}
    <section className="bg-sand-50">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
        <div className="grid gap-6 lg:grid-cols-3">
          <Card title="About &amp; how to use it" footer={<Link to="/about">Read the guide →</Link>}>
            <p>
              Roles, getting started, the dataset catalogue, data requests, uploading data, and
              what to do when something looks wrong. No technical background assumed.
            </p>
          </Card>
          <Card title="Security" footer={<Link to="/security">How data is protected →</Link>}>
            <p>
              How keys are managed, how data is encrypted, how access is controlled, and what is
              logged. For anyone who needs confidence in the system before entrusting data to it.
            </p>
          </Card>
          <Card title="Roadmap" footer={<Link to="/roadmap">Where things stand →</Link>}>
            <p>
              What is built and working today, described as it actually stands rather than as we
              would like it to be.
            </p>
          </Card>
        </div>
      </div>
    </section>

    {/* Access */}
    <section className="border-t border-sand-200 bg-white">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8">
        <div className="flex flex-col items-start gap-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-5">
            <Mark className="h-11 w-11 shrink-0" />
            <div className="max-w-xl">
              <h2 className="font-display text-2xl text-ink">Accounts are by invitation</h2>
              <p className="mt-2 leading-relaxed text-ink-600">
                You cannot sign yourself up. Your principal investigator invites you by email address.
                The invitation cannot be used by anyone else. Two-factor authentication is mandatory
                for every account.
              </p>
              <p className="site-prose mt-3 text-[0.9375rem]">
                Would your department or research unit like its own space in the system? Write to{" "}
                <Mail address={CONTACT_EMAIL} subject="New department in Nafnleyndarkerfi" />.
              </p>
            </div>
          </div>
          <Link
            to="/login"
            className="shrink-0 rounded-lg bg-ink px-6 py-3 text-sm font-semibold text-white transition hover:bg-ink-800"
          >
            Log in
          </Link>
        </div>
      </div>
    </section>
  </>
);
